import { z } from 'zod';
/**
 * Account status vocabulary for the Users import (AIMSIS-19376).
 *
 * `admin.ts` already READS `status` on a user document (treating a missing value
 * as `active` and `archived` as retired); the import is the first writer, so the
 * accepted values are pinned here and shared by the template, the parser, and the
 * client's dropdowns. Keeping one list is what makes "controlled value" mean the
 * same thing in all three places.
 */
export const UserAccountStatus = z.enum(['active', 'inactive', 'archived']);
/**
 * Role vocabulary offered by the import template. The ORDER is the ticket's own
 * (`student`, `teacher`, `proctor`, `admin`) so the dropdown reads the way the
 * requirement was written. `satisfies readonly Role[]` pins every value to the
 * roles the API accepts at compile time, and the API test suite asserts the two
 * sets are EQUAL — so this list can neither admit an invalid role nor silently
 * drop a valid one.
 */
export const USERS_IMPORT_ROLE_VALUES = ['student', 'teacher', 'proctor', 'admin'];
export const USERS_IMPORT_STATUS_VALUES = UserAccountStatus.options;
/**
 * Every field a workbook column can resolve to. `ignore` is an EXPLICIT
 * "this column is not imported" — distinct from omitting the column, which lets
 * the parser re-apply its own alias detection. Without it an admin cannot undo a
 * wrong auto-detection, because the decision would travel as absence and be
 * overwritten by the alias on the next parse.
 */
export const UsersImportField = z.enum([
    'name', 'email', 'role', 'status', 'studentNumber', 'nip', 'ignore',
]);
export const UsersImportColumnMapping = z.object({
    column: z.number().int().nonnegative().max(63),
    field: UsersImportField,
});
/**
 * 10 MB is the ticket's upload ceiling. Base64 inflates by 4/3, so the wire
 * payload is bounded at 14 MB — the same relationship the Question Bank importer
 * uses for its 5 MB file limit.
 */
export const MAX_USERS_WORKBOOK_BYTES = 10_000_000;
export const MAX_USERS_WORKBOOK_BASE64 = 14_000_000;
export const PreviewUsersWorkbookRequest = z.object({
    fileName: z.string().trim().min(1).max(255),
    workbookBase64: z.string().min(1).max(MAX_USERS_WORKBOOK_BASE64),
    /** The admin's explicit column assignments, overriding alias detection. */
    fieldMapping: z.array(UsersImportColumnMapping).max(64).optional(),
}).strict();
export const ConfirmUsersWorkbookRequest = z.object({
    digest: z.string().regex(/^[a-f0-9]{64}$/),
    /**
     * Finalization stays blocked while any row is not `passed`. The admin clears
     * the block either by fixing the file and previewing again, or by explicitly
     * importing only the passed rows.
     */
    skipInvalidRows: z.boolean().default(false),
}).strict();
export const UsersImportIdParams = z.object({
    importId: z.string().uuid(),
});
//# sourceMappingURL=user-import.js.map